import React from 'react'
import styled from 'styled-components'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import { useDispatch, useSelector } from 'react-redux'
import { updateUserRoutine, updateUserLocalRoutine } from 'features/auth/ducks/actions'
import { selectFeatureVisibility, selectComplianceEnabled } from 'features/auth/ducks/selectors'
import { FEATURE_GROUPS, isFeatureVisible } from 'constants/featureGroups'

const FeatureSwitchBar = ({ surface, isOwner, compact = false }) => {
  const dispatch = useDispatch()
  const featureVisibility = useSelector(selectFeatureVisibility)
  const complianceEnabled = useSelector(selectComplianceEnabled)

  const groups = FEATURE_GROUPS.filter(g => (surface === 'dashboard' ? g.widgets.length > 0 : g.passport))

  const isOn = groupKey => groupKey === 'compliance'
    ? complianceEnabled
    : isFeatureVisible(featureVisibility, groupKey, isOwner)

  const onToggle = groupKey => {
    const next = !isOn(groupKey)
    const merged = {}
    FEATURE_GROUPS.forEach(g => {
      const v = featureVisibility?.[g.key]
      if (v === true || v === false) merged[g.key] = v
    })
    merged[groupKey] = next
    dispatch(updateUserLocalRoutine({ featureVisibility: merged }))
    dispatch(updateUserRoutine({ featureVisibility: merged, __silent: true }))
  }

  return (
    <Bar $compact={compact}>
      <Head>
        <Title $compact={compact}>Show or hide areas</Title>
        {!compact && <Hint>Switch off what you don't need for a simpler view.</Hint>}
      </Head>
      <Chips>
        {groups.map(g => {
          const on = isOn(g.key)
          const Icon = on ? VisibilityRoundedIcon : VisibilityOffRoundedIcon
          return (
            <Chip
              key={g.key}
              type='button'
              $on={on}
              $compact={compact}
              onClick={() => onToggle(g.key)}
              aria-pressed={on}
              title={on ? `${g.label} is showing — click to hide` : `${g.label} is hidden — click to show`}
            >
              <Icon style={{ fontSize: compact ? 14 : 17 }} />
              <span>{g.label}</span>
            </Chip>
          )
        })}
      </Chips>
    </Bar>
  )
}

export default FeatureSwitchBar

const Bar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e9e9ee;
  border-radius: 14px;
  ${({ $compact }) => $compact && 'flex-direction: row; align-items: center; flex-wrap: wrap; gap: 6px 10px; padding: 0; margin-bottom: 0; border: none; box-shadow: none; background: transparent;'}
`

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`

const Title = styled.span`
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};

  ${({ $compact }) => $compact && `
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #8A8F9C;
  `}
`

const Hint = styled.span`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Chip = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 15px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 700;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
  border: 1.5px solid ${({ $on }) => ($on ? '#7ed4de' : 'transparent')};
  background: ${({ $on }) => ($on ? '#eafafb' : '#f3f4f6')};
  color: ${({ $on, theme }) => ($on ? (theme?.colors?.haiti || '#0C1229') : '#9aa2af')};
  svg { color: ${({ $on }) => ($on ? '#0b8fa0' : '#b3b8c2')}; }
  &:hover {
    border-color: ${({ $on }) => ($on ? '#4ec3d0' : '#d7dae1')};
    background: ${({ $on }) => ($on ? '#e2f8fa' : '#eceef1')};
  }

  ${({ $compact }) => $compact && `
    height: 28px;
    padding: 0 10px;
    gap: 5px;
    font-size: 12px;
    border-width: 1px;
  `}
`
